import { ReactNode, useState } from 'react';
import AssignmentIndIcon from '@mui/icons-material/AssignmentIndTwoTone';
import RemoveIcon from '@mui/icons-material/HighlightOffTwoTone';
import { IconButton, Tooltip } from '@mui/material';
import { gridRowSelectionIdsSelector, useGridApiContext } from '@mui/x-data-grid-pro';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { getApiErrorMessage } from '@api/getApiError';
import { batchLegRequest } from '@api/Trinium/Endpoints/Leg';
import { usePutLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { LegStatus } from '@api/Trinium/types';
import { queueModal } from '@components/Modals/modalSlice';
import DynamicSelectContainer from '@components/DynamicSelectContainer';
import DriverButtonListContent from '@forms/Driver/ButtonListContent';
import { AssignedDriver } from '@forms/Driver/types';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch } from '@reduxStore';

import styles from './DriverDropdownSlot.module.css';

interface DriverDropdownCellSlotProps {
  /** Leg Identifier of the relevant */
  legId: number;
  /** Leg Status of the relevant */
  legStatus: keyof typeof LegStatus;
  /** Display text of the cell, Driver Name */
  cellText?: string;
  /** Flag indicating the leg requires Hazmat Certification */
  legRequiresHazmatCertification?: boolean;
  /** Flag indicating to display Reminder when update is made */
  isReminder?: boolean;
  /** Tooltip content shown when hovering the Driver Name */
  tooltip?: ReactNode;
}

/**
 * Component for rendering Driver Name as a clickable dropdown to assign/change
 * the Driver in a MUI DataGrid RenderCell slot.
 */
export default function DriverDropdownCellSlot(props: DriverDropdownCellSlotProps) {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const gridApiRef = useGridApiContext();
  const [putLegs] = usePutLegsMutation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | SVGElement | null>(null);
  const legStatus = getEnumValueByKey(LegStatus, props.legStatus) as LegStatus;

  const handleRemoveClick = () => {
    void batchLegRequest([
      {
        id: props.legId,
        actionType: {
          action: 'undo'
        }
      }
    ]);
  };

  /**
   * On Change callback passed into the Driver Selector component
   * @param value unknown due to generic used by shared component but we will parse into DriverOnChangeObject
   */
  const onDriverChange = (value: unknown) => {
    if (value && typeof value === 'object' && 'driver' in value) {
      const selectedDriver: AssignedDriver = value as AssignedDriver;

      if (!selectedDriver.driver?.id) {
        throw new Error('Driver is required.');
      }

      // Read the selection only when saving so every driver cell does not subscribe to grid selection state
      const selectedRowIds = gridRowSelectionIdsSelector(gridApiRef);
      const targetLegIds = selectedRowIds.has(props.legId)
        ? Array.from(selectedRowIds.keys())
            .map(Number)
            .filter((legId) => legId > 0)
        : [props.legId];

      void putLegs({
        terminalNumber,
        actionType: {
          action: 'assignResources',
          data: {
            legIds: targetLegIds,
            drvId: selectedDriver.driver.id,
            tractorId: selectedDriver.tractorId ?? undefined
          }
        }
      })
        .unwrap()
        .then((response) => {
          response.errors.forEach((error) => {
            appDispatch(
              appendErrorAnnouncement({
                message: error.errorMessage
              })
            );
          });

          /** Show Reminder Modal if Leg's isReminder is true */
          if (props.isReminder) {
            appDispatch(
              queueModal({
                Component: 'reminderModal',
                Props: {
                  legId: props.legId
                }
              })
            );
          }
        })
        .catch((ex: unknown) => {
          appDispatch(
            appendErrorAnnouncement({
              message: getApiErrorMessage(ex)
            })
          );
        });

      setAnchorEl(null);
    }
  };

  return (
    <div className={styles['container']}>
      <Tooltip title={anchorEl ? '' : props.tooltip}>
        <div
          className={`${styles['driver-container']} ${anchorEl ? styles['selected'] : ''}`}
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
          }}>
          {!props.cellText ? (
            <AssignmentIndIcon
              color='info'
              fontSize='inherit'
              className={styles['unassigned-ico']}
            />
          ) : (
            <span className={styles['display-text']}>{props.cellText}</span>
          )}
        </div>
      </Tooltip>
      {props.cellText && [LegStatus.NA, LegStatus.AV, LegStatus.OS].includes(legStatus) && (
        <IconButton
          className={styles['remove-btn']}
          onClick={handleRemoveClick}>
          <RemoveIcon fontSize='inherit' />
        </IconButton>
      )}
      <DynamicSelectContainer
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        className={styles['driver-list-popup']}>
        <DriverButtonListContent
          onChange={onDriverChange}
          closeMenu={() => {
            setAnchorEl(null);
          }}
          mode={{
            Drivers: {
              enabled: true
            },
            Tractors: {
              enabled: true
            },
            Carriers: {
              enabled: true
            },
            Salespeople: {
              enabled: false
            },
            requiresHazmat: props.legRequiresHazmatCertification
          }}
        />
      </DynamicSelectContainer>
    </div>
  );
}
